import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { blogAPI, getImageUrl } from "../../services/api";
import Reveal from "../Shared/Reveal";
import lower_back from "../../assets/img/reasons-for-lower-back-pain.webp";
import "./BlogDetail.scss";
import { normalizeRichText } from "../../Utils/stripHtml";

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogPost();
  }, [id]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await blogAPI.getById(id);
      setPost(response.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Blog post not found' : 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="blog-detail-loading">
        <div className="loading-message">Loading blog post...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-detail-error">
        <div className="error-message">
          <h2>Blog Post Not Found</h2>
          <p>{error || 'The blog post you are looking for does not exist.'}</p>
          <Link to="/blog" className="site__button site__button_style--solid site__button_shape--default">
            <span className="text">Back to Blog</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="main main_blog_detail">
      <div className="hero-width0">
        <div id="beforemain_wrap">
          <main id="main-areaarticle">
            <article id="main-area">
              <section
                className="compSection blog-detail-section textcolor__default"
                style={{ paddingTop: "100px", paddingBottom: "100px" }}
              >
                <div className="blog-detail-container">
                  {/* Blog Header */}
                  <div className="blog-detail-header">
                    {post.category && (
                      <span className="post__cat">{post.category}</span>
                    )}
                    <h1 className="blog-detail-title">{post.title}</h1>
                    <div className="blog-detail-meta">
                      {post.author && (
                        <span className="post__author">by {post.author}</span>
                      )}
                      {post.publishedDate && (
                        <span className="post__date">{formatDate(post.publishedDate)}</span>
                      )}
                    </div>
                  </div>

                  {/* Featured Image */}
                  {post.image && (
                    <Reveal as="div" direction="fade" className="blog-detail-image">
                      <img
                        src={getImageUrl(post.image)}
                        alt={post.title}
                        onError={(e) => {
                          e.target.src = lower_back;
                        }}
                      />
                    </Reveal>
                  )}

                  {/* Blog Content */}
                  <Reveal as="div" direction="up" className="blog-detail-content">
                    {post.excerpt && (
                      <div className="blog-excerpt">
                        <p>{post.excerpt}</p>
                      </div>
                    )}
                    <div 
                      className="blog-content"
                      dangerouslySetInnerHTML={{ __html: normalizeRichText(post.content) }}
                    />
                  </Reveal>

                  {/* Back to Blog Button */}
                  <div className="blog-detail-footer">
                    <Link to="/blog" className="site__button site__button_style--solid site__button_shape--default">
                      <span className="text">Back to Blog</span>
                    </Link>
                  </div>
                </div>
              </section>
            </article>
          </main>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
